import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Upload,
  Loader2,
  X,
  Package,
  FileText,
  Mail,
  Phone,
  Calendar as CalIcon,
  Clock,
  CheckCircle2,
  FileDown,
} from "lucide-react";
import { API } from "@/lib/api";

const CATEGORIES = ["Audio", "Luci", "Video", "DJ & FX", "Strutture & Truss"];

const emptyForm = {
  id: null,
  name: "",
  category: "Audio",
  daily_price: 0,
  quantity: 1,
  description: "",
  image_path: null,
};

export default function CollabDashboard() {
  const { collab, logout } = useAuth();
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadItems = async () => {
    setLoading(true);
    const { data } = await api.get("/items/mine");
    setItems(data);
    setLoading(false);
  };
  const loadQuotes = async () => {
    setLoading(true);
    const { data } = await api.get("/collaborators/quotes");
    setQuotes(data);
    setLoading(false);
  };

  useEffect(() => {
    if (tab === "items") loadItems();
    else loadQuotes();
  }, [tab]);

  const startCreate = () => {
    setForm(emptyForm);
    setShowForm(true);
  };
  const startEdit = (it) => {
    setForm({ ...it });
    setShowForm(true);
  };

  const submitItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        daily_price: Number(form.daily_price),
        quantity: Number(form.quantity),
        description: form.description || "",
        image_path: form.image_path,
      };
      if (form.id) {
        await api.put(`/items/${form.id}`, payload);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/items", payload);
        toast.success("Articolo aggiunto");
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await api.delete(`/items/${id}`);
    toast.success("Articolo eliminato");
    loadItems();
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_path: data.path }));
      toast.success("Foto caricata");
    } catch {
      toast.error("Errore caricamento foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500">
            Area partner
          </div>
          <h1 className="font-heading text-2xl sm:text-4xl font-black uppercase tracking-tight text-white break-words">
            {collab?.name || "Service"}
          </h1>
          <div className="text-xs text-zinc-500 mt-1 font-mono">
            {collab?.email}
          </div>
        </div>
        <button
          data-testid={testIds.collabLogoutBtn}
          onClick={logout}
          className="min-h-[40px] px-4 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-rose-800 text-zinc-300 hover:text-rose-500 text-xs font-semibold uppercase tracking-wider transition flex items-center gap-1.5"
        >
          <LogOut size={14} /> Esci
        </button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        <button
          data-testid={testIds.collabTabItems}
          onClick={() => setTab("items")}
          className={`min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "items"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Package size={14} /> Mio materiale ({items.length})
        </button>
        <button
          data-testid={testIds.collabTabQuotes}
          onClick={() => setTab("quotes")}
          className={`min-h-[44px] px-4 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-1.5 ${
            tab === "quotes"
              ? "border-rose-600 text-white"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <FileText size={14} /> Miei preventivi
        </button>
      </div>

      {tab === "items" && (
        <>
          <button
            data-testid={testIds.collabAddItemBtn}
            onClick={startCreate}
            className="mb-4 min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs sm:text-sm shadow-lg shadow-rose-900/40 transition flex items-center gap-2"
          >
            <Plus size={16} /> Aggiungi articolo
          </button>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
              Nessun articolo. Aggiungi il tuo materiale.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 sm:p-4 flex items-center gap-3"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                    {it.image_path ? (
                      <img
                        src={fileUrl(it.image_path)}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package size={20} className="text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-white text-sm truncate">
                      {it.name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span
                        data-testid={testIds.collabItemStatus(it.id)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border flex items-center gap-1 ${
                          it.status === "approved"
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                            : "bg-amber-500/15 text-amber-300 border-amber-500/30"
                        }`}
                      >
                        {it.status === "approved" ? (
                          <>
                            <CheckCircle2 size={10} /> pubblicato
                          </>
                        ) : (
                          <>
                            <Clock size={10} /> in verifica
                          </>
                        )}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {it.category}
                      </span>
                      <span className="text-[10px] font-mono text-zinc-600">
                        · qty {it.quantity}
                      </span>
                      <span className="font-mono font-bold text-emerald-400 text-xs">
                        · €{it.daily_price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => startEdit(it)}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition"
                    aria-label="Modifica"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => deleteItem(it.id)}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-300 hover:text-rose-400 transition"
                    aria-label="Elimina"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "quotes" && (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-zinc-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Caricamento...
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
              Nessun preventivo ricevuto per il tuo materiale.
            </div>
          ) : (
            quotes.map((q) => {
              const myItemIds = new Set(items.map((i) => i.id));
              const myLines = q.lines.filter((l) => myItemIds.has(l.item_id));
              const dates =
                q.event_start_date && q.event_end_date
                  ? q.event_start_date === q.event_end_date
                    ? q.event_start_date
                    : `${q.event_start_date} → ${q.event_end_date}`
                  : "-";
              return (
                <div
                  key={q.id}
                  className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-white text-base break-words">
                        {q.customer_name}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-500">
                        {q.customer_email && (
                          <a
                            href={`mailto:${q.customer_email}`}
                            className="flex items-center gap-1 hover:text-rose-400 break-all"
                          >
                            <Mail size={11} /> {q.customer_email}
                          </a>
                        )}
                        {q.customer_phone && (
                          <a
                            href={`tel:${q.customer_phone}`}
                            className="flex items-center gap-1 hover:text-rose-400"
                          >
                            <Phone size={11} /> {q.customer_phone}
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <CalIcon size={11} /> {dates}
                        </span>
                        <span className="font-mono">
                          {q.days} {q.days === 1 ? "giorno" : "giorni"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-zinc-800 pt-3 space-y-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-rose-400 mb-1">
                      Tuoi articoli richiesti
                    </div>
                    {myLines.map((l, i) => (
                      <div
                        key={i}
                        className="flex justify-between gap-2 text-xs text-zinc-300 font-mono"
                      >
                        <span className="break-words min-w-0 flex-1">
                          {l.quantity}× {l.name}
                        </span>
                        <span className="shrink-0">
                          €{(l.daily_price * l.quantity).toFixed(2)}/gg
                        </span>
                      </div>
                    ))}
                  </div>
                  {q.notes && (
                    <div className="mt-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-lg p-3 break-words">
                      {q.notes}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex justify-end">
                    <button
                      data-testid={testIds.collabQuotePartnerPdf(q.id)}
                      onClick={() => {
                        fetch(`${API}/quotes/${q.id}/partner-pdf`, {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem("spectrum_collab_token")}`,
                          },
                        })
                          .then((r) => {
                            if (!r.ok) throw new Error("err");
                            return r.blob();
                          })
                          .then((b) => window.open(URL.createObjectURL(b), "_blank"))
                          .catch(() => toast.error("Errore download PDF"));
                      }}
                      className="min-h-[36px] px-3 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5"
                    >
                      <FileDown size={12} /> Il mio PDF (netto)
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={submitItem}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-black uppercase text-white">
                {form.id ? "Modifica articolo" : "Nuovo articolo"}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Nome articolo
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Categoria
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Prezzo €/gg
                  </label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.daily_price}
                    onChange={(e) =>
                      setForm({ ...form, daily_price: e.target.value })
                    }
                    className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Quantità disponibile
                </label>
                <input
                  required
                  type="number"
                  min={0}
                  value={form.quantity}
                  onChange={(e) =>
                    setForm({ ...form, quantity: e.target.value })
                  }
                  className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Descrizione
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="w-full px-3.5 py-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Foto articolo
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-lg bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                    {form.image_path ? (
                      <img
                        src={fileUrl(form.image_path)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package size={22} className="text-zinc-700" />
                    )}
                  </div>
                  <label className="flex-1 min-h-[44px] flex items-center justify-center gap-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider cursor-pointer transition">
                    {uploading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Caricamento...
                      </>
                    ) : (
                      <>
                        <Upload size={14} />
                        {form.image_path ? "Cambia foto" : "Carica foto"}
                      </>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={uploadImage}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 min-h-[48px] rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider transition"
              >
                Annulla
              </button>
              <button
                type="submit"
                className="flex-1 min-h-[48px] rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition"
              >
                {form.id ? "Salva" : "Aggiungi"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
